export default class AudioProctorService {
  constructor({ onEvent } = {}) {
    this.onEvent = onEvent || (() => {});
    this.audioContext = null;
    this.analyser = null;
    this.source = null;
    this.running = false;
    this.rafId = null;
    this.primaryProfile = null;
    this.dualVoiceSince = 0;
    this.calibrationStartedAt = 0;
  }

  start(stream) {
    if (!stream || this.running || typeof window === 'undefined') return false;

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return false;

      this.audioContext = new AudioContextClass();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;
      this.source = this.audioContext.createMediaStreamSource(stream);
      this.source.connect(this.analyser);
      this.running = true;
      this.calibrationStartedAt = Date.now();
      this.loop();
      return true;
    } catch {
      return false;
    }
  }

  loop = () => {
    if (!this.running || !this.analyser) return;

    const bins = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(bins);
    const sampleRate = this.audioContext.sampleRate;
    const binWidth = sampleRate / this.analyser.fftSize;

    const bandEnergy = (minHz, maxHz) => {
      const start = Math.max(0, Math.floor(minHz / binWidth));
      const end = Math.min(bins.length - 1, Math.ceil(maxHz / binWidth));
      let total = 0;
      for (let index = start; index <= end; index += 1) {
        total += bins[index];
      }
      return total / Math.max(1, end - start + 1);
    };

    const band1 = bandEnergy(85, 255);
    const band2 = bandEnergy(255, 600);
    const now = Date.now();

    if (!this.primaryProfile && now - this.calibrationStartedAt >= 30000 && band1 > 20) {
      this.primaryProfile = { band1, band2 };
    }

    if (this.primaryProfile && band1 > 25 && band2 > this.primaryProfile.band2 * 0.6) {
      if (!this.dualVoiceSince) {
        this.dualVoiceSince = now;
      } else if (now - this.dualVoiceSince >= 3000) {
        this.onEvent('multiple_voices', {
          band1: Number(band1.toFixed(2)),
          band2: Number(band2.toFixed(2)),
          durationMs: now - this.dualVoiceSince,
        });
        this.dualVoiceSince = now;
      }
    } else {
      this.dualVoiceSince = 0;
    }

    this.rafId = requestAnimationFrame(this.loop);
  };

  stop() {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.source?.disconnect();
    this.analyser?.disconnect();
    this.audioContext?.close?.();
    this.source = null;
    this.analyser = null;
    this.audioContext = null;
    this.primaryProfile = null;
    this.dualVoiceSince = 0;
  }
}
